import { createContext, useCallback, useContext, useRef, useSyncExternalStore } from "react";
import type { SepiaElement } from "../../../shared/model";
import type { Editor, Overlay, StyleState, UiState } from "../editor";

export const EditorContext = createContext<Editor | null>(null);

export function useEditor(): Editor {
  const ed = useContext(EditorContext);
  if (!ed) throw new Error("useEditor outside EditorContext");
  return ed;
}

export function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || !a || !b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!Object.is(a[i], b[i])) return false;
    return true;
  }
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (!Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false;
  return true;
}

/** useSyncExternalStore with a memoised selector (re-renders only when the selected value changes). */
export function useStore<T>(subscribe: (fn: () => void) => () => void, select: () => T, eq: (a: T, b: T) => boolean = shallowEqual): T {
  const last = useRef<{ v: T } | null>(null);
  const get = () => {
    const next = select();
    if (last.current && eq(last.current.v, next)) return last.current.v;
    last.current = { v: next };
    return next;
  };
  return useSyncExternalStore(subscribe, get, get);
}

export function useUi<T>(sel: (ui: UiState, ed: Editor) => T): T {
  const ed = useEditor();
  const sub = useCallback((fn: () => void) => ed.subscribeUi(fn), [ed]);
  return useStore(sub, () => sel(ed.ui, ed));
}

/** Like useUi, but also re-reads when the document changes (layer names, visibility...). */
export function useBoard<T>(sel: (ui: UiState, ed: Editor) => T): T {
  const ed = useEditor();
  const sub = useCallback(
    (fn: () => void) => {
      const offs = [ed.subscribeUi(fn), ed.doc.subscribe((c) => c.structural && fn())];
      return () => offs.forEach((o) => o());
    },
    [ed]
  );
  return useStore(sub, () => sel(ed.ui, ed));
}

export function useStyle<T>(sel: (s: StyleState) => T): T {
  const ed = useEditor();
  const sub = useCallback((fn: () => void) => ed.subscribeStyle(fn), [ed]);
  return useStore(sub, () => sel(ed.style));
}

export function useOverlay<T>(sel: (o: Overlay) => T): T {
  const ed = useEditor();
  const sub = useCallback((fn: () => void) => ed.subscribeOverlay(fn), [ed]);
  return useStore(sub, () => sel(ed.overlay));
}

export function useCamera<T>(sel: (c: { x: number; y: number; z: number; width: number; height: number }) => T): T {
  const ed = useEditor();
  const sub = useCallback((fn: () => void) => ed.camera.subscribe(fn), [ed]);
  return useStore(sub, () => sel(ed.camera));
}

export function useElement(id: string): SepiaElement | undefined {
  const ed = useEditor();
  const sub = useCallback((fn: () => void) => ed.subscribeElement(id, fn), [ed, id]);
  return useSyncExternalStore(sub, () => ed.getEl(id), () => ed.getEl(id));
}

/** Re-render when the document changes (structure only by default). */
export function useDocVersion(structuralOnly = true): number {
  const ed = useEditor();
  const sub = useCallback(
    (fn: () => void) =>
      ed.doc.subscribe((c) => {
        if (!structuralOnly || c.structural) fn();
      }),
    [ed, structuralOnly]
  );
  return useSyncExternalStore(sub, () => (structuralOnly ? ed.doc.structureVersion : ed.doc.version), () => 0);
}

export function useHistoryState(): { canUndo: boolean; canRedo: boolean } {
  const ed = useEditor();
  const sub = useCallback((fn: () => void) => ed.history.subscribe(fn), [ed]);
  return useStore(sub, () => ({ canUndo: ed.history.canUndo, canRedo: ed.history.canRedo }));
}
